import React, {useState} from 'react';

import HorizontalBarChart from 'components/HorizontalBarChart';
import simulateTestResult from 'api/simulateTestResult';
import strings from 'utils/strings';

import './styles.scss';

const SUBTEST_IDS = [1, 2, 3, 4, 5, 6];

const getSubtestLabel = (id) => strings.controlPanel[`simulateSubtest${id}`];

const SUBTEST_MAXES = {
	1: 25,
	2: 112,
	3: 21,
	4: 80,
	5: 8,
	6: 18,
};

const GRADE_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

const emptyScores = () =>
	SUBTEST_IDS.reduce((acc, id) => ({...acc, [id]: ''}), {});

function SimulateTestResult() {
	const [grade, setGrade] = useState('');
	const [gender, setGender] = useState('');
	const [isForeign, setIsForeign] = useState('');
	const [scores, setScores] = useState(emptyScores());
	const [result, setResult] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const DECIMAL_SUBTESTS = new Set([4]);

	const getScoreError = (id, val) => {
		if (val === '' || val === null || val === undefined) return strings.common.required || 'Required';
		const n = Number(val);
		const max = SUBTEST_MAXES[id];
		const invalidNumber = DECIMAL_SUBTESTS.has(id)
			? isNaN(n)
			: !Number.isInteger(n);
		if (invalidNumber || n < 0 || n > max) {
			return `0–${max}`;
		}
		return null;
	};

	const scoreErrors = SUBTEST_IDS.reduce((acc, id) => {
		const err = getScoreError(id, scores[id]);
		if (err) acc[id] = err;
		return acc;
	}, {});

	const isValid =
		grade !== '' &&
		gender !== '' &&
		isForeign !== '' &&
		Object.keys(scoreErrors).length === 0;

	const handleScoreChange = (id, value) => {
		setScores((prev) => ({...prev, [id]: value}));
		setResult(null);
	};

	const handleScoreBlur = (id, value) => {
		if (value === '') return;
		const n = Number(value);
		if (isNaN(n)) return;
		const max = SUBTEST_MAXES[id];
		const fixed = DECIMAL_SUBTESTS.has(id) ? n : Math.floor(n);
		const clamped = Math.min(Math.max(fixed, 0), max);
		setScores((prev) => ({...prev, [id]: String(clamped)}));
	};

	const handleSubmit = () => {
		if (!isValid) return;
		setLoading(true);
		setError(null);
		setResult(null);

		simulateTestResult({
			testType: 'child-screening',
			language: 'se',
			grade: Number(grade),
			gender,
			isForeign: Number(isForeign),
			subtestScores: SUBTEST_IDS.reduce(
				(acc, id) => ({...acc, [id]: Number(scores[id])}),
				{}
			),
		})
			.then((res) => {
				setResult(res);
				setLoading(false);
			})
			.catch((err) => {
				setError(
					(err && err.message) || strings.controlPanel.simulateGenericError
				);
				setLoading(false);
			});
	};

	return (
		<div className="lit-simulate-test">
			<div className="lit-simulate-test__form">
				<div className="lit-simulate-test__context">
					<div className="lit-simulate-test__field">
						<label className="lit-simulate-test__label">
							{strings.controlPanel.simulateTestType}
						</label>
						<span className="lit-simulate-test__static-value">
							{strings.controlPanel.simulateTestTypeValue}
						</span>
					</div>

					<div className="lit-simulate-test__field">
						<label className="lit-simulate-test__label">
							{strings.controlPanel.simulateGrade}
						</label>
						<select
							className="lit-input__field lit-simulate-test__select"
							value={grade}
							onChange={(e) => {
								setGrade(e.target.value);
								setResult(null);
							}}
						>
							<option value="">{strings.controlPanel.simulateSelectGrade}</option>
							{GRADE_OPTIONS.map((g) => (
								<option key={g} value={g}>
									{g}
								</option>
							))}
						</select>
					</div>

					<div className="lit-simulate-test__field">
						<label className="lit-simulate-test__label">
							{strings.controlPanel.simulateGender}
						</label>
						<select
							className="lit-input__field lit-simulate-test__select"
							value={gender}
							onChange={(e) => {
								setGender(e.target.value);
								setResult(null);
							}}
						>
							<option value="">{strings.controlPanel.simulateSelectGender}</option>
							<option value="m">{strings.controlPanel.simulateGenderMale}</option>
							<option value="f">{strings.controlPanel.simulateGenderFemale}</option>
						</select>
					</div>

					<div className="lit-simulate-test__field">
						<label className="lit-simulate-test__label">
							{strings.controlPanel.simulateForeign}
						</label>
						<select
							className="lit-input__field lit-simulate-test__select"
							value={isForeign}
							onChange={(e) => {
								setIsForeign(e.target.value);
								setResult(null);
							}}
						>
							<option value="">{strings.controlPanel.simulateSelect}</option>
							<option value="0">{strings.controlPanel.simulateForeignNo}</option>
							<option value="1">{strings.controlPanel.simulateForeignYes}</option>
						</select>
					</div>
				</div>

				<div className="lit-simulate-test__subtests">
					<h3 className="lit-simulate-test__subtests-heading">
						{strings.controlPanel.simulateSubtestsHeading}
					</h3>
					{SUBTEST_IDS.map((id) => {
						const max = SUBTEST_MAXES[id];
						const err = scoreErrors[id];
						const touched = scores[id] !== '';

						return (
							<div
								key={id}
								className="lit-simulate-test__subtest-row"
							>
								<label className="lit-simulate-test__subtest-label">
									{getSubtestLabel(id)}
								</label>
								<div className="lit-simulate-test__subtest-input-wrap">
									<input
										type="number"
										className={`lit-input__field lit-simulate-test__score-input${
											touched && err
												? ' lit-simulate-test__score-input--error'
												: ''
										}`}
										value={scores[id]}
										min={0}
										max={max}
										step={DECIMAL_SUBTESTS.has(id) ? 'any' : 1}
										onChange={(e) =>
											handleScoreChange(id, e.target.value)
										}
										onBlur={(e) =>
											handleScoreBlur(id, e.target.value)
										}
									/>
									<span className="lit-simulate-test__max">
										{`/ ${max}`}
									</span>
								</div>
								{touched && err && (
									<span className="lit-simulate-test__field-error">
										{`${err}`}
									</span>
								)}
							</div>
						);
					})}
				</div>

				<button
					className="lit-btn lit-btn--small bg-primary lit-simulate-test__submit"
					disabled={!isValid || loading}
					onClick={handleSubmit}
				>
					{loading ? strings.controlPanel.simulateSubmitting : strings.controlPanel.simulateSubmit}
				</button>

				{error && (
					<div className="lit-simulate-test__error-message">
						{error}
					</div>
				)}
			</div>

			{result && result.subtests && (
				<div className="lit-simulate-test__results">
					<h3 className="lit-simulate-test__results-heading">
						{strings.controlPanel.simulateResultsHeading}
					</h3>
					<div className="lit-simulate-test__chart">
						<HorizontalBarChart
							subtests={result.subtests}
							testType="child-screening"
						/>
					</div>
					<ul className="lit-simulate-test__summary">
						{result.subtests.map((subtest) => {
							const max = SUBTEST_MAXES[subtest.id];
							return (
								<li
									key={subtest.id}
									className="lit-simulate-test__summary-row"
								>
									<div className="lit-simulate-test__summary-name">
										{getSubtestLabel(subtest.id)}
									</div>
									<div className="lit-simulate-test__summary-raw">
										{`${subtest.totalCorrect} / ${max}`}
									</div>
									<div className="lit-simulate-test__summary-stats">
										<span>
											<strong>z:</strong> {String(subtest.zScore)}
										</span>
										<span>
											<strong>%ile:</strong>{' '}
											{String(subtest.percentile)}
										</span>
										<span>
											<strong>T:</strong> {String(subtest.tScore)}
										</span>
									</div>
								</li>
							);
						})}
					</ul>
				</div>
			)}
		</div>
	);
}

export default SimulateTestResult;
